import { useState, useEffect } from 'react';

// Standard loading steps (roofing workflow)
const standardSteps = [
  "Checking deck condition...",
  "Scraping off snots from deck...",
  "Applying primer...",
  "Cutting VCL...",
  "Laying VCL...",
  "Positioning insulation boards...",
  "Warming up the torch...",
  "Rolling out membrane...",
  "Welding seams...",
  "Dressing upstands...",
  "Final inspection...",
  "Ready to quote!"
];

// Rare/funny messages (max 1 per day)
const rareSteps = [
  "Arguing with the site manager...",
  "Trying to achieve the ridiculous programme...",
  "Filling in the hot works permit...",
  "Waiting for the sun to come out...",
  "Kettle's on...",
  "Finding the tape measure...",
  "Checking the weather forecast...",
  "Convincing the apprentice it's not lunch yet...",
  "Dodging the pigeons...",
  "Pretending to read the method statement...",
  "Looking for phone signal...",
  "Avoiding the safety officer...",
  "Calculating tea break duration...",
  "Searching for the right colour weld rod..."
];

// Get loading steps with potential rare message
function getLoadingSteps() {
  const steps = [...standardSteps];
  
  // Check if we've shown a rare step today
  const today = new Date().toDateString();
  const lastRareShown = localStorage.getItem('flatquote_rare_shown');
  
  if (lastRareShown !== today && Math.random() < 0.5) {
    // 50% chance to show a rare step
    const rareStep = rareSteps[Math.floor(Math.random() * rareSteps.length)];
    
    // Insert at random position (not first or last)
    const insertAt = Math.floor(Math.random() * (steps.length - 2)) + 1;
    steps.splice(insertAt, 0, rareStep);
    
    localStorage.setItem('flatquote_rare_shown', today);
  }
  
  return steps;
}

export function LoadingScreen({ onReady }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [steps] = useState(() => getLoadingSteps());
  const [apiReady, setApiReady] = useState(false);

  // Ping API to wake it up (handles Render cold starts)
  useEffect(() => {
    const API_URL = import.meta.env.VITE_API_URL || '';
    
    if (!API_URL) {
      // No API configured, skip check
      setApiReady(true);
      return;
    }

    fetch(`${API_URL}/health`)
      .then(() => setApiReady(true))
      .catch(() => setApiReady(true)); // Continue anyway
  }, []);

  // Cycle through loading steps
  useEffect(() => {
    if (currentStep >= steps.length - 1 && apiReady) {
      // Finished steps and API ready
      setTimeout(() => onReady(), 500);
      return;
    }

    const timer = setTimeout(() => {
      if (currentStep < steps.length - 1) {
        setCurrentStep(prev => prev + 1);
      } else if (apiReady) {
        onReady();
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [currentStep, apiReady, steps, onReady]);

  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center">
      <div className="text-center p-8 max-w-md">
        {/* Logo */}
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-amber-500 rounded-2xl mb-4 shadow-lg">
            <span className="text-slate-900 font-bold text-3xl">FQ</span>
          </div>
          <h1 className="text-2xl font-bold text-white">FlatQuote</h1>
          <p className="text-slate-400 text-sm mt-1">Professional Flat Roofing Estimates</p>
        </div>

        {/* Progress bar */}
        <div className="w-full bg-slate-700 rounded-full h-2 mb-4 overflow-hidden">
          <div 
            className="bg-amber-500 h-2 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Current step */}
        <p className="text-slate-300 text-lg min-h-[28px] w-full transition-opacity duration-200">
          {steps[currentStep]}
        </p>
      </div>
    </div>
  );
}

export default LoadingScreen;
